
import AdministrationRoutes from '@/router/Administration';
import { Component, Vue } from 'vue-property-decorator';
import { State } from 'vuex-class';

// Utils
import { hasAllPermissions, hasCurrentRoutePermission } from '@/utils/Permissions';

// Interfaces
import AppSidebarNavigationLink from '@/ship/Models/AppSidebarNavigationLink';
import User from '@/ship/Models/User';

// Enums
import ApplicationEventNames from '@/ship/Enums/ApplicationEventNames';

// SVG
import BriefcaseSVG from '@/assets/briefcase.svg';
import ClockSVG from '@/assets/clock.svg';
import LocationMarkerSVG from '@/assets/location-marker.svg';
import NotificationsBell from '@/assets/notifications_bell.svg';
import OfficeBuildingSVG from '@/assets/office-building.svg';
import SettingsGearSVG from '@/assets/settings-gear.svg';
import UserGroupSVG from '@/assets/user-group.svg';

// Components
import { VAppFooter, VAppHeader, VAppLayout, VAppMain, VAppSidebar, VAppSidebarNavigation } from '@/components/VApp';

import VErrorViewer from '@/components/VErrorViewer.vue';

@Component({
    components: {
        VAppLayout,
        VAppHeader,
        VAppMain,
        VAppSidebar,
        VAppFooter,
        VAppSidebarNavigation,

        VErrorViewer,
    },
})
export default class AdministrationPage extends Vue {
    @State user!: User | null;

    public isLoading: boolean = false;

    readonly linksList: AppSidebarNavigationLink[] = [
        {
            label: this.$t('organisations'),
            to: { name: 'administration.organisations' },
            icon: OfficeBuildingSVG,
            permissions: this.findRoutePermissions('administration.organisations'),
        },
        {
            label: this.$t('projects'),
            to: { name: 'administration.projects' },
            icon: BriefcaseSVG,
            permissions: this.findRoutePermissions('administration.projects'),
        },
        {
            label: this.$t('users'),
            to: { name: 'administration.users' },
            icon: UserGroupSVG,
            permissions: this.findRoutePermissions('administration.users'),
            children: [
                {
                    label: this.$t('replacement'),
                    to: { name: 'administration.replacement' },
                    icon: ClockSVG,
                    permissions: this.findRoutePermissions('administration.replacement'),
                },
            ],
        },
        {
            label: this.$t('cities'),
            to: { name: 'administration.cities' },
            icon: LocationMarkerSVG,
            permissions: this.findRoutePermissions('administration.cities'),
        },
        {
            label: this.$t('notifications'),
            to: { name: 'administration.notifications' },
            icon: NotificationsBell,
            children: [
                {
                    label: this.$t('services.designing'),
                    to: { name: 'administration.designing' },
                    icon: SettingsGearSVG,
                },
                {
                    label: 'Сметы',
                    to: { name: 'administration.estimate' },
                    icon: SettingsGearSVG,
                },
                {
                    label: 'Предписания',
                    to: { name: 'administration.prescription' },
                    icon: SettingsGearSVG,
                },
            ],
        },
    ];

    get filteredLinksList(): AppSidebarNavigationLink[] {
        const filteredFirstDepth = this.filterLinksList(this.linksList);
        return filteredFirstDepth.map((link) => ({
            ...link,
            children: link.children && this.filterLinksList(link.children),
        }));
    }

    get hasRoutePermission(): boolean {
        return hasCurrentRoutePermission();
    }

    public findRoutePermissions(name: string) {
        return AdministrationRoutes[0].children.find((page) => page.name === name)?.meta.permissions;
    }

    public setLoading(state: boolean) {
        this.isLoading = state;
    }

    filterLinksList(linksList: AppSidebarNavigationLink[]) {
        return linksList.filter((link) => {
            return link.children || (link.permissions ? hasAllPermissions(link.permissions) : true);
        });
    }

    public async created() {
        this.$bus.on(ApplicationEventNames.SetAdministrationLoading, this.setLoading);

        // redirect to the first available page if the route is administration
        if (this.$route.name === 'administration' && this.filteredLinksList.length) {
            this.$router.replace(this.filteredLinksList[0].to);
        }
    }

    public beforeDestroy() {
        this.$bus.off(ApplicationEventNames.SetAdministrationLoading, this.setLoading);
    }
}
